export type ComplaintCategory =
  | "HARASSMENT"
  | "DISCRIMINATION"
  | "RETALIATION"
  | "WAGE_HOUR"
  | "SAFETY"
  | "MANAGER_MISCONDUCT"
  | "COWORKER_CONFLICT"
  | "POLICY_VIOLATION"
  | "ETHICS_FRAUD"
  | "WORKPLACE_BULLYING"
  | "OTHER";

export type ComplaintSeverity = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

export type ComplaintStatus =
  | "NEW"
  | "TRIAGED"
  | "UNDER_REVIEW"
  | "INVESTIGATION"
  | "AWAITING_INFO"
  | "RESOLVED"
  | "DISMISSED"
  | "CLOSED";

export type ComplaintEventType =
  | "CREATED"
  | "TRIAGED"
  | "ASSIGNED"
  | "STATUS_CHANGED"
  | "COMMENT_ADDED"
  | "EVIDENCE_UPLOADED"
  | "WATCHER_ADDED"
  | "WATCHER_REMOVED"
  | "RESOLVED"
  | "DISMISSED";

export interface Complaint {
  id: string;
  org_id: string;
  title: string;
  description: string;
  category: ComplaintCategory;
  severity: ComplaintSeverity;
  status: ComplaintStatus;
  is_anonymous: boolean;
  submitted_by: string | null;
  employee_id: string | null;
  assigned_to: string | null;
  resolution_notes: string | null;
  resolution_outcome?: string | null;
  resolved_at: string | null;
  triaged_at?: string | null;
  acknowledged_at?: string | null;
  expected_resolution_at?: string | null;
  dismissed_reason?: string | null;
  dismissed_at?: string | null;
  created_at: string;
  updated_at: string;
  submitter_name?: string | null;
  assignee_name?: string | null;
}

export interface ComplaintEvent {
  id: string;
  complaint_id: string;
  actor_user_id?: string | null;
  event_type: ComplaintEventType;
  payload_json: Record<string, unknown> | null;
  is_visible_to_reporter: boolean;
  created_at: string;
}

export interface ComplaintComment {
  id: string;
  complaint_id?: string;
  author_user_id?: string | null;
  author_name?: string | null;
  body: string;
  is_internal: boolean;
  created_at: string;
}

export interface ComplaintEvidence {
  id: string;
  complaint_id?: string;
  uploaded_by?: string | null;
  uploader_name?: string | null;
  filename: string;
  content_type?: string | null;
  size_bytes?: number | null;
  is_sensitive: boolean;
  url?: string | null;
  created_at: string;
}

export interface ComplaintWatcher {
  id?: string;
  complaint_id?: string;
  user_id: string;
  user_name?: string | null;
  created_at?: string;
}

export interface ComplaintCreateResponse {
  id: string;
  message: string;
  anonymous_token: string | null;
}

export interface ComplaintTriagePayload {
  severity?: ComplaintSeverity;
  category?: ComplaintCategory;
  expected_resolution_at?: string;
  assigned_to?: string;
}

export interface ComplaintResolvePayload {
  resolution_outcome: string;
  resolution_notes?: string;
}

export interface ComplaintDismissPayload {
  reason: string;
}

export interface AnonymousStatusView {
  id: string;
  title: string;
  status: ComplaintStatus;
  severity: ComplaintSeverity;
  category: ComplaintCategory;
  created_at: string;
  triaged_at?: string | null;
  resolved_at?: string | null;
  events: ComplaintEvent[];
}

export interface ComplaintCategoryBreakdown {
  category: ComplaintCategory;
  count: number;
}

export const COMPLAINT_CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  HARASSMENT: "Harassment",
  DISCRIMINATION: "Discrimination",
  RETALIATION: "Retaliation",
  WAGE_HOUR: "Wage & Hour",
  SAFETY: "Safety",
  MANAGER_MISCONDUCT: "Manager Misconduct",
  COWORKER_CONFLICT: "Coworker Conflict",
  POLICY_VIOLATION: "Policy Violation",
  ETHICS_FRAUD: "Ethics / Fraud",
  WORKPLACE_BULLYING: "Workplace Bullying",
  OTHER: "Other",
};

export const COMPLAINT_SEVERITY_LABELS: Record<ComplaintSeverity, string> = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
  URGENT: "Urgent",
};

export const COMPLAINT_STATUS_LABELS: Record<ComplaintStatus, string> = {
  NEW: "New",
  TRIAGED: "Triaged",
  UNDER_REVIEW: "Under Review",
  INVESTIGATION: "Investigation",
  AWAITING_INFO: "Awaiting Info",
  RESOLVED: "Resolved",
  DISMISSED: "Dismissed",
  CLOSED: "Closed",
};

export const COMPLAINT_EVENT_LABELS: Record<ComplaintEventType, string> = {
  CREATED: "Complaint created",
  TRIAGED: "Triaged",
  ASSIGNED: "Assigned",
  STATUS_CHANGED: "Status changed",
  COMMENT_ADDED: "Comment added",
  EVIDENCE_UPLOADED: "Evidence uploaded",
  WATCHER_ADDED: "Watcher added",
  WATCHER_REMOVED: "Watcher removed",
  RESOLVED: "Resolved",
  DISMISSED: "Dismissed",
};

export const SEVERITY_BADGE_VARIANT: Record<
  ComplaintSeverity,
  "default" | "info" | "warning" | "danger" | "success"
> = {
  LOW: "default",
  MEDIUM: "info",
  HIGH: "warning",
  URGENT: "danger",
};

export const STATUS_BADGE_VARIANT: Record<
  ComplaintStatus,
  "default" | "info" | "warning" | "danger" | "success"
> = {
  NEW: "info",
  TRIAGED: "info",
  UNDER_REVIEW: "warning",
  INVESTIGATION: "warning",
  AWAITING_INFO: "warning",
  RESOLVED: "success",
  DISMISSED: "default",
  CLOSED: "default",
};
